import { type Href, useRouter } from 'expo-router';
import { useFormContext, useWatch } from 'react-hook-form';
import CountStepper from '@/components/FormFields/CountStepper';
import WelcomeStepFrame, {
  WelcomeStepScrollContent,
} from '@/components/Welcome/WelcomeStepFrame';
import {
  WelcomeActions,
  WelcomeCard,
  WelcomePrimaryAction,
} from '@/components/Welcome/WelcomeUI';
import type { WelcomeFormValues } from '@/layouts/Welcome/form';
import generateGames from '@/utils/generateGames';

const MIN_SCHEDULE_OPTION = 1;
const MAX_SCHEDULE_OPTION = 3;

type WelcomeOrgScheduleOptionsProps = {
  previewRoute?: Href;
};

export default function WelcomeOrgScheduleOptions({
  previewRoute = '/welcome/org/schedule/preview',
}: WelcomeOrgScheduleOptionsProps) {
  const { navigate } = useRouter();
  const { control, setValue } = useFormContext<WelcomeFormValues>();
  const seasonDraftId = useWatch({ control, name: 'seasonDraftId' });
  const teamRows = useWatch({ control, name: 'orgTeamRows' });
  const scheduleSlots = useWatch({ control, name: 'scheduleSlots' });
  const matchupCount = useWatch({ control, name: 'scheduleMatchupCount' });
  const repeatEveryWeeks = useWatch({
    control,
    name: 'scheduleRepeatEveryWeeks',
  });
  const teams = teamRows
    .map((teamRow) => ({
      id: teamRow.id,
      title: teamRow.title.trim(),
      home_primary_color: teamRow.color,
      avatar_id: null,
    }))
    .filter((team) => team.title.length > 0);
  const filledSlots = scheduleSlots
    .filter((slot) => slot.datetime.trim().length > 0)
    .map((slot) => ({
      datetime: slot.datetime.trim(),
      location: slot.location.trim() || null,
    }));
  const isValid =
    matchupCount > 0 &&
    repeatEveryWeeks > 0 &&
    Boolean(seasonDraftId) &&
    teams.length > 1 &&
    filledSlots.length > 0;

  const setScheduleOption = (
    field: 'scheduleMatchupCount' | 'scheduleRepeatEveryWeeks',
    value: number,
  ) => {
    setValue(field, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('generatedScheduleGames', [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  const decreaseMatchupCount = () => {
    setScheduleOption(
      'scheduleMatchupCount',
      Math.max(matchupCount - 1, MIN_SCHEDULE_OPTION),
    );
  };
  const increaseMatchupCount = () => {
    setScheduleOption(
      'scheduleMatchupCount',
      Math.min(matchupCount + 1, MAX_SCHEDULE_OPTION),
    );
  };
  const decreaseRepeatEveryWeeks = () => {
    setScheduleOption(
      'scheduleRepeatEveryWeeks',
      Math.max(repeatEveryWeeks - 1, MIN_SCHEDULE_OPTION),
    );
  };
  const increaseRepeatEveryWeeks = () => {
    setScheduleOption(
      'scheduleRepeatEveryWeeks',
      Math.min(repeatEveryWeeks + 1, MAX_SCHEDULE_OPTION),
    );
  };

  const onPressPreview = () => {
    if (!isValid) {
      return;
    }

    const { games } = generateGames({
      parentId: seasonDraftId,
      teams,
      initialGames: filledSlots,
      numberOfMatchups: matchupCount,
      repeatEvery: { weeks: repeatEveryWeeks },
      byeWeeks: true,
    });

    setValue('generatedScheduleGames', games, {
      shouldDirty: true,
      shouldValidate: true,
    });
    navigate(previewRoute);
  };

  return (
    <WelcomeStepFrame
      title="Choose schedule options"
      subtitle="These defaults create a simple round-robin schedule you can review before saving."
      footer={
        <WelcomeActions>
          <WelcomePrimaryAction
            label="Review games"
            disabled={!isValid}
            onPress={onPressPreview}
          />
        </WelcomeActions>
      }
    >
      <WelcomeStepScrollContent>
        <WelcomeCard>
          <CountStepper
            decrementAccessibilityLabel="Decrease matchup count"
            incrementAccessibilityLabel="Increase matchup count"
            label="How many times should each team play each other?"
            max={MAX_SCHEDULE_OPTION}
            min={MIN_SCHEDULE_OPTION}
            onDecrement={decreaseMatchupCount}
            onIncrement={increaseMatchupCount}
            testID="welcome-schedule-matchup-count"
            value={matchupCount}
          />

          <CountStepper
            decrementAccessibilityLabel="Decrease weeks between games"
            incrementAccessibilityLabel="Increase weeks between games"
            label="How many weeks between games?"
            max={MAX_SCHEDULE_OPTION}
            min={MIN_SCHEDULE_OPTION}
            onDecrement={decreaseRepeatEveryWeeks}
            onIncrement={increaseRepeatEveryWeeks}
            testID="welcome-schedule-repeat-weeks"
            value={repeatEveryWeeks}
          />
        </WelcomeCard>
      </WelcomeStepScrollContent>
    </WelcomeStepFrame>
  );
}
